import { Fragment, PureComponent, ReactNode } from 'react';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Table } from 'antd';
import Fuse from 'fuse.js';
const { Column } = Table;
import { DeleteOutlined } from '@ant-design/icons';
import { Button } from 'antd';

import { Actions } from '../../../../../store/actions';
import { GlobalState } from '../../../../../store/reducers';
import {
    getAllSymDiaProperties,
    getDiagnosis,
    getPatientPersonalProfile,
    getSymptoms,
} from '../../../../../store/selectors/prescriptionPad';
import {
    DiagnosisEntityOrSymptomsEntity,
    PatientPersonalProfile,
} from '../../../../../store/types/prescription';
import { fetchMatchingSymDia } from '../../../../../api';
import { ONE_CHAR_GENDER } from '../../../../../pages/NewPatient';
import { getGArray } from '../../../../../store/idbHandler';
import { GSymptomsDiagnosis } from '../../../../../store/db';
import { DB_TABLES } from '../../../../../store/dbTables';
import {
    copySinceInDy,
    copySinceInSy,
    deleteDiagnose,
    deleteSysptom,
    swapPxElement,
    upsertDiagnose,
    upsertSymptom,
} from '../../../../../store/actions/prescriptionPad';
import { randomString, getUnit } from '../../../../../store/utils';
import { LOCAL_ID_PREFIX } from '../../../../../constants';
import { PropertyGroupEntity, SINCE_ID } from '../../../../../api/fetchSymDiaProperties';
import PROPERTIES from '../properties';
import SymDiaAutoSuggestV2, { SY_DY_V2_AS_INPUT } from './SymDiaAutoSuggestV2';
import AntSelect from './AntSelect';
import SymDiaPropertyInput from './SymDiaPropertyInput';
import { generateSinceOptions } from '../../../../common/optionGeneratorUtils';
import { CHANNEL_SUGGESTION, SELECT, SYMPTOM, track } from '../../../../../tracking';
import { EditableCell, EditableCellV2 } from './MedTableRenderer';
import { DiagnosisSuggestionsWithLocal } from './SymDiaV2';
import MoreOptionsModal from './MoreOptionsModal';
import { PAD_V2_CONTAINER } from '../PadContainer';
import { DragHandle, SortableContainer, SortableItem } from './TableWithDragableRows';
import { RenderCellAutosuggestItemJsx } from '../LabVitalTable';
import { SYMPTOMS_KIND, DIAGNOSIS_KIND } from '../../../trackSource';
import { handleTableDragWidth } from '../../../../common/TableUtils';
import { SortStart } from 'react-sortable-hoc';
import CopyToAll from './CopyToAll';
import {
    getGenieSetting,
    getIsICDSuggestionsEnabled,
} from '../../../../../store/selectors/session';
import { EkaFocosableElement } from '../../../../common/types/EkaFocusable';
import { getMyDbId } from '../../../../../api/mactiveid';

type State = {
    gElements?: ReadonlyArray<GSymptomsDiagnosis>;
    moreOptionIndex?: number;
};

type CompProps = {
    kind: typeof SYMPTOMS_KIND | typeof DIAGNOSIS_KIND;
};

interface Props extends CompProps {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    elements: ReadonlyArray<DiagnosisEntityOrSymptomsEntity>;
    patientProfile?: PatientPersonalProfile;
    properties: { [k: string]: PropertyGroupEntity[] };
    propertiesFocusSkip?: boolean;
    isICDSuggestionsEnable?: boolean;
}

const fuseConfig = { keys: ['name'], threshold: 0.2 };

class SyDyTableV2 extends PureComponent<Props, State> {
    REF_MAP: EkaFocosableElement[][] = [];
    fuse = new Fuse([] as GSymptomsDiagnosis[], fuseConfig);

    upsertElementAction = this.props.kind === SYMPTOMS_KIND ? upsertSymptom : upsertDiagnose;
    deleteElementAction = this.props.kind === SYMPTOMS_KIND ? deleteSysptom : deleteDiagnose;
    copySinceAction = this.props.kind === SYMPTOMS_KIND ? copySinceInSy : copySinceInDy;

    state: State = {};

    dragContainerId = randomString(4);

    componentDidMount = async (): Promise<void> => {
        const gElements = await getGArray<GSymptomsDiagnosis>(
            getMyDbId() || '',
            this.props.kind === SYMPTOMS_KIND ? DB_TABLES['symptoms'] : DB_TABLES['diagnosis'],
        );
        this.fuse = new Fuse(gElements, fuseConfig);
        this.setState({
            gElements: gElements,
        });
    };

    onSortEnd = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
        if (oldIndex !== newIndex) {
            this.props.dispatch(
                swapPxElement(
                    this.props.kind === SYMPTOMS_KIND ? 'symptoms' : 'diagnosis',
                    oldIndex,
                    newIndex,
                ),
            );
        }
    };

    onSortStart = (params: SortStart) => {
        // get sample width and apply to dragging row, as it is not fixed, relatie to viewport instead of container

        handleTableDragWidth(params, this.dragContainerId);
    };

    // TODO
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    DraggableContainer = (props) => (
        <SortableContainer
            id={this.dragContainerId}
            useDragHandle
            onSortEnd={this.onSortEnd}
            onSortStart={this.onSortStart}
            {...props}
        />
    );

    // TODO
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    DraggableBodyRow = ({ className, style, ...restProps }) => {
        const { elements, kind } = this.props;
        // function findIndex base on Table rowKey props and should always be a right array index
        const index = elements.findIndex(
            (x, index) => `${kind}-${index}` === restProps['data-row-key'],
        );
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return <SortableItem className="editable-row" index={index} {...restProps} />;
    };

    components = {
        body: {
            wrapper: this.DraggableContainer,
            row: this.DraggableBodyRow,
        },
    };

    elementSuggestApi = async (text: string): Promise<{ id: string; name: string }[]> => {
        const { patientProfile, kind } = this.props;
        try {
            const res = await fetchMatchingSymDia(
                text,
                patientProfile?.gender
                    ? ONE_CHAR_GENDER[patientProfile.gender.toLowerCase()]
                    : undefined,
            );
            return (kind === SYMPTOMS_KIND ? res.symptoms : res.diagnoses) || [];
        } catch (e) {
            return [];
        }
    };

    createOption = (value: string) => ({
        id: `${LOCAL_ID_PREFIX}${
            this.props.kind === SYMPTOMS_KIND ? 'sym' : 'dia'
        }-${new Date().getTime()}-${randomString(4)}`,
        name: value.trim(),
    });

    handleSearch = async (value: string): Promise<{ id: string; name: string }[]> => {
        const { elements } = this.props;

        const all = await buildOptions<{ id: string; name: string }>(
            value,
            elements,
            this.state.gElements || [],
            this.elementSuggestApi,
            this.fuse,
            this.createOption,
        );

        return all;
    };

    onElementSelect = (
        index: number,
        option?: { id: string; name: string; icd10_code?: string; icd10_name?: string },
    ) => {
        const { elements } = this.props;

        const data = option || { id: '', name: '' };
        if (elements[index].id === option?.id) {
            this.handleNextFocus(data, index, 0);
            return;
        }

        this.props.dispatch(this.upsertElementAction(data, index));

        setTimeout(() => {
            this.handleNextFocus(data, index, 0);
        }, 300);
    };

    getIsStaticProperties = (data: DiagnosisEntityOrSymptomsEntity): boolean => {
        const { properties } = this.props;
        if (data.properties && Object.keys(data.properties).length > 0) {
            return false;
        }
        return (
            ((data.duration &&
                (data.duration.custom || (data.duration.unit && data.duration.value))) ||
                data.severity ||
                data.status ||
                data.remark) !== undefined
        );
    };

    updateElement = (data: DiagnosisEntityOrSymptomsEntity) => {
        this.props.dispatch(this.upsertElementAction(data));
    };

    getDefaultProperties = () => {
        const { kind } = this.props;
        return kind === SYMPTOMS_KIND
            ? PROPERTIES.symptom.MdbProperties
            : PROPERTIES.diagnosis.MdbProperties;
    };

    renderP = (elIndex: number, at: 1 | 2) => {
        const { elements } = this.props;

        const data = elements[elIndex];
        const { properties: syDyProperties, duration: syDyDuration } = data;

        const isStaticProperties = this.getIsStaticProperties(data);

        if (isStaticProperties) {
            if (at === 1) {
                const onSearch = async (
                    text: string,
                ): Promise<{ id: string; label: string; meta?: any }[]> => {
                    return new Promise((resolve) => {
                        resolve(generateSinceOptions(text));
                    });
                };

                return (
                    <Fragment>
                        <EditableCell
                            containerId={PAD_V2_CONTAINER}
                            fieldName={`sdp1-${elIndex}`}
                            label="Since"
                            withoutError
                            setRef={(ref) => {
                                this.setPropertyInputRef(ref, elIndex, 1);
                            }}
                            customDropdownClass="font-lato"
                            placeholder="Since"
                            debounceDelay={0}
                            onSearch={onSearch}
                            getLabelToCompare={(o) => o.label}
                            selectedValue={{
                                id: data.duration?.custom
                                    ? data.duration.custom
                                    : data.duration?.value
                                    ? `${data.duration.value} ${data.duration.unit} `
                                    : '',
                                label: data.duration?.custom
                                    ? data.duration.custom
                                    : data.duration?.value
                                    ? `${data.duration.value} ${data.duration.unit} `
                                    : '',
                            }}
                            onSelect={(option) => {
                                this.updateElWithProperty(
                                    {
                                        ...data,
                                        duration: {
                                            custom: option?.label,
                                            unit: getUnit(option?.meta.unit, option?.meta.input),
                                            value: option?.meta?.input,
                                        },
                                    },
                                    elIndex,
                                    1,
                                );
                                track('PX_ELEMENT_PROPERTY', {
                                    // eId: data.id,
                                    eName: data.name,
                                    // pId: p.id,
                                    pName: 'Since',
                                    isStaticP: 'true',
                                    action: option?.label ? 'SET' : 'UNSET',
                                    kind: this.props.kind,
                                });
                            }}
                        />
                        {syDyDuration && (
                            <CopyToAll
                                id={`copy-duration-v2-${this.props.kind}-${elIndex}`}
                                onClick={() => this.onCopyDuration(elIndex)}
                                tableId={this.props.kind}
                            />
                        )}
                    </Fragment>
                );
            } else if (at === 2) {
                const { kind } = this.props;

                return (
                    <EditableCellV2
                        defaultValue={
                            kind === SYMPTOMS_KIND
                                ? data?.severity?.display || ''
                                : data?.status?.display || ''
                        }
                        label={kind === SYMPTOMS_KIND ? 'Severity' : 'Status'}
                        setRef={(ref) => this.setPropertyInputRef(ref, elIndex, 2)}
                    >
                        <AntSelect
                            containerIdToAttachDropdown={PAD_V2_CONTAINER}
                            className="bg-transparent"
                            showArrow={false}
                            defaultOpen
                            setRef={(ref) => {
                                this.setPropertyInputRef(ref, elIndex, 2);
                            }}
                            style={{ width: '100%' }}
                            autoFocus
                            allowClear
                            placeholder={kind === SYMPTOMS_KIND ? 'Severity' : 'Status'}
                            optionFilterProp="children"
                            options={
                                kind === SYMPTOMS_KIND
                                    ? PROPERTIES.symptom.Severity
                                    : PROPERTIES.diagnosis.Status
                            }
                            setValue={(option) => {
                                this.updateElWithProperty(
                                    kind === SYMPTOMS_KIND
                                        ? {
                                              ...data,
                                              severity: option
                                                  ? {
                                                        code: option.id,
                                                        display: option.name,
                                                    }
                                                  : undefined,
                                          }
                                        : {
                                              ...data,
                                              status: option
                                                  ? {
                                                        code: option.id,
                                                        display: option.name,
                                                    }
                                                  : undefined,
                                          },
                                    elIndex,
                                    2,
                                );
                                track('PX_ELEMENT_PROPERTY', {
                                    // eId: data.id,
                                    eName: data.name,
                                    // pId: p.id,
                                    pName: kind === SYMPTOMS_KIND ? 'Severity' : 'Status',
                                    isStaticP: 'true',
                                    action: option ? 'SET' : 'UNSET',
                                    kind: this.props.kind,
                                });
                            }}
                            // onSearch={onSearch}
                            filterOption={true}
                            value={
                                kind === SYMPTOMS_KIND
                                    ? data.severity
                                        ? {
                                              id: data.severity?.code || '',
                                              name: data?.severity?.display || '',
                                          }
                                        : undefined
                                    : data.status
                                    ? {
                                          id: data.status?.code || '',
                                          name: data?.status?.display || '',
                                      }
                                    : undefined
                            }
                            getValue={(o) => o?.id}
                            getLabelToCompare={(o) => o.name}
                        />
                    </EditableCellV2>
                );
            } else {
                return null;
            }
        }

        const { properties, kind } = this.props;
        const syProperties = properties[data.id] || this.getDefaultProperties();
        if (syProperties && syProperties.length > 0 && at === 1) {
            return (
                <Fragment>
                    <SymDiaPropertyInput
                        containerId={PAD_V2_CONTAINER}
                        data={data}
                        p={syProperties[0]}
                        update={(data) => this.updateElWithProperty(data, elIndex, 1)}
                        setRef={(ref) => this.setPropertyInputRef(ref, elIndex, 1)}
                        kind={kind}
                    />
                    {syDyProperties?.[SINCE_ID] && (
                        <CopyToAll
                            id={`copy-duration-v2-${this.props.kind}-${elIndex}`}
                            onClick={() => this.onCopyDuration(elIndex)}
                            tableId={this.props.kind}
                        />
                    )}
                </Fragment>
            );
        } else if (syProperties && syProperties.length > 1 && at === 2) {
            return (
                <SymDiaPropertyInput
                    containerId={PAD_V2_CONTAINER}
                    editableDiv={true}
                    data={data}
                    p={syProperties[1]}
                    update={(data) => this.updateElWithProperty(data, elIndex, 2)}
                    setRef={(ref) => this.setPropertyInputRef(ref, elIndex, 2)}
                    kind={kind}
                />
            );
        }

        return null;
    };

    setPropertyInputRef = (ref: EkaFocosableElement, elIndex: number, propertyIndex: number) => {
        if (!this.REF_MAP[elIndex]) {
            this.REF_MAP[elIndex] = [];
        }

        this.REF_MAP[elIndex][propertyIndex] = ref;
        return;
    };

    updateElWithProperty = (
        data: DiagnosisEntityOrSymptomsEntity,
        elIndex: number,
        propertyIndex: number,
    ) => {
        this.updateElement(data);

        this.handleNextFocus(data, elIndex, propertyIndex);
    };

    handleNextFocus = (
        data: DiagnosisEntityOrSymptomsEntity,
        elIndex: number,
        propertyIndex: number,
    ) => {
        const currentActiveRow = this.REF_MAP[elIndex];

        let isCleared = false;

        let didTheJob = false;

        if (this.getIsStaticProperties(data)) {
            isCleared =
                propertyIndex === 0
                    ? !data.id || !data.name
                    : propertyIndex === 1
                    ? !data.duration?.custom
                    : !data.status?.display && !data.severity?.display;
        } else {
            const elProperties = this.props.properties && this.props.properties[data.id];
            if (propertyIndex === 0) {
                isCleared = !data.id || !data.name;
            } else if (elProperties && elProperties.length > propertyIndex - 1) {
                isCleared =
                    !data.properties ||
                    !data.properties[elProperties[propertyIndex - 1].id]?.selection ||
                    data.properties[elProperties[propertyIndex - 1].id]?.selection?.length === 0;
            }
        }

        if (isCleared) {
            return;
        }

        currentActiveRow[propertyIndex].blur();

        if (propertyIndex < currentActiveRow.length - 1) {
            // Todo make this generic
            if (this.getIsStaticProperties(data)) {
                if (propertyIndex === 0) {
                    currentActiveRow[propertyIndex + 1].focus();
                    didTheJob = true;
                } else if (propertyIndex === 1) {
                    if (!(data.severity?.display || data.status?.display)) {
                        currentActiveRow[propertyIndex + 1].focus();
                    }
                    didTheJob = true;
                } else if (propertyIndex === 2) {
                    currentActiveRow[propertyIndex + 1].focus();
                    didTheJob = true;
                }
            } else {
                const { properties } = this.props;
                const nextPropertyKey =
                    properties &&
                    properties[data.id] &&
                    properties[data.id][propertyIndex - 1 + 1].id;
                const temp =
                    nextPropertyKey &&
                    data.properties &&
                    data.properties[nextPropertyKey] &&
                    data.properties[nextPropertyKey].selection;
                if (temp && temp.length > 0 && temp[0].value) {
                } else {
                    currentActiveRow[propertyIndex + 1].focus();
                }
                didTheJob = true;
            }
        }

        if (!didTheJob && elIndex === this.props.elements.length - 1) {
            this.focusAS();
        }
    };

    setMoreOptionsIndex = (index?: number) => {
        track('SYM_DIA_MORE_OPTIONS', { index: `${index}` });
        this.setState({
            moreOptionIndex: index,
        });
    };

    getMoreOptionBtText = (
        element: DiagnosisEntityOrSymptomsEntity,
    ): { name: string; value: string }[] => {
        if (this.getIsStaticProperties(element)) {
            const obj = element.remark
                ? [
                      {
                          name: 'remark',
                          value: element.remark,
                      },
                  ]
                : [];

            if (element.properties) {
                const temp = Object.keys(element.properties);
                temp.forEach((pId) => {
                    const value = element.properties?.[pId].selection
                        ?.filter((s) => s.value)
                        ?.map((s) => `${s.value}${s.unit ? ` ${s.unit}` : ''}`)
                        .join(' | ');
                    value &&
                        obj.push({
                            name: element.properties?.[pId].name || '',
                            value: value,
                        });
                });
            }
            return obj;
        }

        const { properties } = this.props;

        if (!element.properties) {
            return [];
        }

        const elProperties = properties[element.id] || this.getDefaultProperties();

        const temp = Object.keys(element.properties)
            // filter top two properties
            .filter((id) => id !== (elProperties[0] || {}).id && id !== (elProperties[1] || {}).id);

        const propertiesValues: { name: string; value: string }[] = [];
        temp.forEach((pId) => {
            const value = element.properties?.[pId].selection
                ?.filter((s) => s.value)
                ?.map((s) => `${s.value}${s.unit ? ` ${s.unit}` : ''}`)
                .join(' | ');
            value &&
                propertiesValues.push({
                    name: element.properties?.[pId].name || '',
                    value: value,
                });
        });

        return propertiesValues || [];
    };

    renderCellAutosuggestItem = (option: DiagnosisSuggestionsWithLocal) => {
        const { isICDSuggestionsEnable } = this.props;
        return (
            <RenderCellAutosuggestItemJsx
                option={
                    isICDSuggestionsEnable
                        ? {
                              ...option,
                              name: `${option?.name}${
                                  option?.icd10_code ? ` - ${option?.icd10_code}` : ''
                              }`,
                          }
                        : option
                }
                wrapperClassName={
                    this.props.kind === SYMPTOMS_KIND ? 'sy-dropdown-item' : 'dx-dropdown-item'
                }
                icon={this.props.kind === SYMPTOMS_KIND ? 'Sx' : 'Dx'}
                iconWrapperClassName={this.props.kind === SYMPTOMS_KIND ? 'circle-sy' : 'circle-dx'}
            />
        );
    };

    ELEMENT = this.props.kind === SYMPTOMS_KIND ? SYMPTOM : DIAGNOSIS_KIND;

    onElementSelected = (element: DiagnosisSuggestionsWithLocal): void => {
        const { dispatch, kind } = this.props;
        dispatch((kind === SYMPTOMS_KIND ? upsertSymptom : upsertDiagnose)(element));
    };

    onElementSuggetionSelected = (element: DiagnosisSuggestionsWithLocal): void => {
        const { propertiesFocusSkip } = this.props;
        track(SELECT, { element: this.ELEMENT, channel: CHANNEL_SUGGESTION });
        this.onElementSelected(element);

        setTimeout(() => {
            if (propertiesFocusSkip) return this.focusAS();
            const { elements } = this.props;
            this.handleNextFocus(elements[elements.length - 1], elements.length - 1, 0);
        }, 500);
    };

    private focusAS() {
        const t = document.getElementById(
            `${SY_DY_V2_AS_INPUT}-${this.props.kind}`,
        ) as HTMLInputElement;
        t && t.focus();
    }

    onCopyDuration = (index: number) => {
        const syDyElement = this.props.elements[index];
        const since = syDyElement.properties?.[SINCE_ID]?.selection?.[0] || syDyElement.duration;
        if (since && since.value) this.props.dispatch(this.copySinceAction(since));
    };
    extractStringFromHTML = (htmlContent: string): string => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, 'text/html');

        const textContent = doc.body.innerText; // keeping innertext for now alternative : textContent but the catch is with inner text if something is hidden that also will be visible.

        return textContent || '';
    };

    render(): ReactNode {
        const { dispatch, elements, properties, kind, isICDSuggestionsEnable } = this.props;
        const { moreOptionIndex } = this.state;

        return (
            <Fragment>
                <MoreOptionsModal
                    data={moreOptionIndex !== undefined ? elements[moreOptionIndex] : undefined}
                    closeMe={() => this.setMoreOptionsIndex()}
                    properties={
                        moreOptionIndex !== undefined
                            ? properties[elements[moreOptionIndex].id] ||
                              this.getDefaultProperties()
                            : []
                    }
                    // TODO this prop can be avaoided
                    isStaticProperties={
                        moreOptionIndex !== undefined
                            ? this.getIsStaticProperties(elements[moreOptionIndex])
                            : undefined
                    }
                    onSave={this.updateElement}
                    kind={kind}
                />

                {elements.length > 0 && (
                    <Table
                        className="sydy-table mx-table mt-8"
                        rowClassName={() => 'editable-row'}
                        bordered={true}
                        showHeader={false}
                        components={this.components}
                        dataSource={elements}
                        pagination={false}
                        rowKey={(record, index) => `${this.props.kind}-${index}`}
                        id={this.props.kind}
                    >
                        <Column
                            dataIndex="name"
                            key="name"
                            className="name"
                            render={(_, __, index) => {
                                return {
                                    // props: { className: '', style: { verticalAlign: 'top' } },
                                    children: (
                                        <div className="flex space-x-16 items-center">
                                            <DragHandle />
                                            <div className="font-700 w-full text-neutral-black text-14 placeholder-neutral-6">
                                                <EditableCell
                                                    fieldName={`sdn-${index}`}
                                                    label={kind}
                                                    setRef={(ref) => {
                                                        this.setPropertyInputRef(ref, index, 0);
                                                    }}
                                                    debounceDelay={200}
                                                    customDropdownClass={`py-8 ${
                                                        kind === SYMPTOMS_KIND
                                                            ? 'sy-dropdown'
                                                            : 'dx-dropdown'
                                                    } rounded-8`}
                                                    optionsRenderer={this.renderCellAutosuggestItem}
                                                    onSearch={this.handleSearch}
                                                    getLabelToCompare={(o) => o.name}
                                                    selectedValue={
                                                        elements[index] && elements[index].id
                                                            ? {
                                                                  id: elements[index].id,
                                                                  name: isICDSuggestionsEnable
                                                                      ? `${elements[index]?.name}${
                                                                            elements[index]
                                                                                ?.icd10_code
                                                                                ? ` - ${elements[index]?.icd10_code}`
                                                                                : ''
                                                                        }`
                                                                      : elements[index].name,
                                                              }
                                                            : undefined
                                                    }
                                                    onSelect={(option) => {
                                                        this.onElementSelect(
                                                            index,
                                                            option
                                                                ? {
                                                                      id: option.id,
                                                                      name: option.name,
                                                                      // @ts-expect-error ts version upgrade
                                                                      icd10_code: option.icd10_code,
                                                                      // @ts-expect-error ts version upgrade
                                                                      icd10_name: option.icd10_name,
                                                                  }
                                                                : undefined,
                                                        );
                                                    }}
                                                    placeholder={`Type ${
                                                        this.props.kind === SYMPTOMS_KIND
                                                            ? 'Symptoms'
                                                            : 'Diagnosis'
                                                    }`}
                                                    containerId={PAD_V2_CONTAINER}
                                                />
                                            </div>
                                        </div>
                                    ),
                                };
                            }}
                        />
                        <Column
                            className="mx-duration properties relative"
                            dataIndex="properties"
                            key="properties"
                            render={(_, __, index) => {
                                return {
                                    // props: {
                                    //     // TODO
                                    //     // adjust based on properties
                                    //     // colSpan: 2,
                                    // },
                                    children: (
                                        <div className="w-full">{this.renderP(index, 1)}</div>
                                    ),
                                };
                            }}
                            ellipsis={true}
                        />
                        <Column
                            className="p1"
                            title={null}
                            onHeaderCell={undefined}
                            dataIndex="p1"
                            key="p1"
                            render={(_, __, index) => {
                                return {
                                    children: this.renderP(index, 2),
                                };
                            }}
                            ellipsis={true}
                        />

                        <Column
                            className="instruction"
                            dataIndex="instruction"
                            key="instruction"
                            render={(_, __, index) => (
                                <div className="flex space-x-16 items-center justify-between w-full">
                                    <div className="space-y-4">
                                        {this.getMoreOptionBtText(elements[index]).length === 0 ? (
                                            <Button
                                                type="default"
                                                className="tracking-4 uppercase font-700 text-darwin-neutral-400 text-12 leading-5"
                                                onClick={() => this.setMoreOptionsIndex(index)}
                                                ref={(ref) => {
                                                    ref && this.setPropertyInputRef(ref, index, 3);
                                                }}
                                            >
                                                More Options
                                            </Button>
                                        ) : (
                                            <div
                                                onClick={() => this.setMoreOptionsIndex(index)}
                                                className="cursor-pointer"
                                            >
                                                {this.getMoreOptionBtText(elements[index]).map(
                                                    (o) => (
                                                        <div className=" space-x-4">
                                                            <span className="text-darwin-neutral-500">
                                                                {o.name}:
                                                            </span>
                                                            <span>
                                                                {/* {o.name === 'Note'
                                                                    ? this.extractStringFromHTML(
                                                                          o.value,
                                                                      )
                                                                    : o.value} */}
                                                                {(() => {
                                                                    const matchedPropertyId = Object.entries(
                                                                        elements[index]
                                                                            ?.properties || {},
                                                                    ).find(
                                                                        ([_, prop]) =>
                                                                            prop.name === o.name,
                                                                    )?.[0];

                                                                    return matchedPropertyId ===
                                                                        'pg-5243973452'
                                                                        ? this.extractStringFromHTML(
                                                                              o.value,
                                                                          )
                                                                        : o.value;
                                                                })()}
                                                            </span>
                                                        </div>
                                                    ),
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    <DeleteOutlined
                                        style={{ color: '#616A8F' }}
                                        size={24}
                                        onClick={() =>
                                            dispatch(this.deleteElementAction(elements[index].id))
                                        }
                                        onPointerEnterCapture={undefined}
                                        onPointerLeaveCapture={undefined}
                                    />
                                </div>
                            )}
                        />
                    </Table>
                )}
                <div className="mt-8 w-full rounded-8 border border-neutral-7 px-12 flex items-center row-input-container relative">
                    <SymDiaAutoSuggestV2 kind={kind} onSelect={this.onElementSuggetionSelected} />
                </div>
            </Fragment>
        );
    }
}

export default connect((state: GlobalState, props: CompProps) => ({
    elements: props.kind === SYMPTOMS_KIND ? getSymptoms(state) : getDiagnosis(state),
    patientProfile: getPatientPersonalProfile(state),
    properties: getAllSymDiaProperties(state),
    propertiesFocusSkip: getGenieSetting(state, 'propertiesFocusSkip') as boolean,
    isICDSuggestionsEnable: getIsICDSuggestionsEnabled(state),
}))(SyDyTableV2);

export const buildOptions = async <T extends { id: string; name: string }>(
    value: string,
    elements: ReadonlyArray<T>,
    gElements: ReadonlyArray<T>,
    elementSuggestApi: (value: string) => Promise<T[]>,
    fuse?: Fuse<T>,
    createOption?: (value: string) => T,
    allowDuplicates?: boolean,
): Promise<T[]> => {
    let all: T[] = [];
    if (!value) {
        const t = gElements
            ? allowDuplicates
                ? [...gElements].filter(Boolean)
                : [...gElements].filter((e) => !elements.find((t) => t.id === e.id))
            : [];
        t.splice(5);
        all = t;
    } else {
        const options = await elementSuggestApi(value);
        const res = allowDuplicates
            ? options.filter(Boolean)
            : options.filter((e) => !elements.find((t) => t.id === e.id));
        const loweredCased = value.trim().toLowerCase();

        if (fuse) {
            all.push(
                ...fuse
                    .search(value)
                    .filter((e) => !elements.find((t) => t.id === e.item.id))
                    .map((i) => i.item),
            );
            for (let index = 0; index < res.length; index++) {
                if (!all.find((t) => t.id === res[index].id)) {
                    all.push(res[index]);
                }
            }
        } else {
            all.push(...res);
        }

        all.splice(5);

        if (
            createOption &&
            !all.find((t) => t.name.toLowerCase() === loweredCased) &&
            value.trim()
        ) {
            all.push(createOption(value));
        }
    }
    return all;
};
